import React, { useState, useEffect, useContext, useCallback } from "react";
import { API } from "aws-amplify";
import NumberFormat from "react-number-format";
import Chart from "react-google-charts";
import { makeStyles, lighten } from "@material-ui/core/styles";
import clsx from "clsx";
import AppBar from "@material-ui/core/AppBar";
import Hidden from "@material-ui/core/Hidden";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Container from "@material-ui/core/Container";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { AppContext } from "../views/App";

const useStyles = makeStyles(theme => ({
  backButton: {
    marginRight: theme.spacing(2)
  },
  title: {
    flexGrow: 1
  },
  container: {
    height: "calc(100vh - 56px)",
    [`${theme.breakpoints.up("xs")} and (orientation: landscape)`]: {
      height: "calc(100vh - 48px)"
    },
    [theme.breakpoints.up("sm")]: {
      height: "calc(100vh - 64px)"
    },
    overflow: "auto"
  },
  empty: {
    minHeight: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4)
  },
  flexRow: {
    display: "flex",
    flexDirection: "row"
  },
  options: {
    flexGrow: 1,
    textAlign: "right",
    padding: theme.spacing(2)
  },
  card: {
    margin: theme.spacing(2),
    width: "100%"
  },
  topCard: {
    borderBottom: "1px solid #efefef",
    borderRight: "1px solid #efefef",
    width: "33%",
    padding: theme.spacing(2, 3)
  },
  topCardPurple: {
    borderBottom: "1px solid #efefef",
    borderRight: "1px solid #efefef",
    width: "33%",
    padding: theme.spacing(2, 3),
    color: "white",
    backgroundColor: "#470a68",
    border: "none"
  },
  noData: {
    display: "flex",
    flexDirection: "column",
    textAlign: "center",
    margin: theme.spacing(10, 5)
  },
  root: {
    width: "100%",
    margin: theme.spacing(0, 2, 2, 2)
  },
  table: {
    minWidth: 750
  },
  chipIngreso: {
    height: "20px",
    color: "#FFF",
    backgroundColor: "#31D2CA",
    fontWeight: "bold"
  },
  chipComision: {
    height: "20px",
    color: "#FFF",
    backgroundColor: "rgb(220, 0, 78)",
    fontWeight: "bold"
  }
}));

const Billetera = ({ onBack }) => {
  const classes = useStyles();
  const { cuenta } = useContext(AppContext);
  const [loading, setLoading] = useState(false);

  const [paymentsTotalLastMonth, setPaymentsTotalLastMonth] = useState([
    ["Fecha", "Montos"],
    [0, 0]
  ]);
  const [paymentsChart, setPaymentsChart] = useState(paymentsTotalLastMonth);
  const [totalPayments, setTotalPayments] = useState(0);

  const [salesTotalLastMonth, setSalesTotalLastMonth] = useState([
    ["Fecha", "Ventas"],
    [0, 0]
  ]);
  const [salesChart, setSalesChart] = useState(salesTotalLastMonth);
  const [totalSales, setTotalSales] = useState(0);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedIndexPayments, setSelectedIndexPayments] = useState(3);

  const [initialRows, setInitialRows] = useState([]);
  const [rows, setRows] = useState([]);
  const order = "desc";
  const orderBy = "folio";
  const selected = [];
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());

  const fetchPaymentsTotalLastMonth = useCallback(async () => {
    setLoading(true);

    try {
      const init = {
        queryStringParameters: {
          cuenta: cuenta.id_cuenta
        }
      };
      const result = await API.get(
        process.env.REACT_APP_HUB_API,
        "/charts/total-ventas-recientes",
        init
      );

      if (result.success) {
        if (result.data.length > 0) {
          let payments = [];

          const thisMonth = new Date(
            new Date(new Date().getFullYear(), new Date().getMonth() - 1, new Date().getDate())
          );

          for (let i = 0; i < result.data.length; i++) {
            let payment = result.data[i];
            let totalPayments = payment.total ? parseInt(payment.total) : 0;

            if (payment.fecha != null && payment.total != null) {
              payments.push([payment.fecha.substring(0, 10), totalPayments]);
            }
          }

          const ventasThisMonth = payments.filter(date => new Date(date[0]) >= thisMonth);

          setPaymentsTotalLastMonth(payments);
          setPaymentsChart([["Fecha", "Ventas"], ...ventasThisMonth]);
          setTotalPayments(total(ventasThisMonth));
          setSelectedIndexPayments(3);
        } else {
          setPaymentsTotalLastMonth([]);
          setPaymentsChart([
            ["Fecha", "Montos"],
            [0, 0]
          ]);
          setTotalPayments(0);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error("fetchTicketsLastMonth", err);
    }
  }, [cuenta]);

  const fetchSalesTotalLastMonth = useCallback(async () => {
    setLoading(true);

    try {
      const init = {
        queryStringParameters: {
          cuenta: cuenta.id_cuenta
        }
      };
      const result = await API.get(
        process.env.REACT_APP_HUB_API,
        "/charts/tickets-ultimo-mes",
        init
      );

      if (result.success) {
        if (result.data.length > 0) {
          let sales = [];

          const thisMonth = new Date(
            new Date(new Date().getFullYear(), new Date().getMonth() - 1, new Date().getDate())
          );

          for (let i = 0; i < result.data.length; i++) {
            let sale = result.data[i];
            let totalSales = sale.cantidad ? parseInt(sale.cantidad) : 0;

            if (sale.fecha != null && sale.cantidad != null) {
              sales.push([sale.fecha.substring(0, 10), totalSales]);
            }
          }

          const salesThisMonth = sales.filter(date => new Date(date[0]) >= thisMonth);

          setSalesTotalLastMonth(sales);
          setSalesChart([["Fecha", "Ventas"], ...salesThisMonth]);
          setTotalSales(total(salesThisMonth));
        } else {
          setSalesTotalLastMonth([]);
          setSalesChart([
            ["Fecha", "Montos"],
            [0, 0]
          ]);
          setTotalSales(0);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error("fetchTicketsLastMonth", err);
    }
  }, [cuenta]);

  const fetchMovimientos = useCallback(async () => {
    try {
      const init = {
        queryStringParameters: {
          cuenta: cuenta.id_cuenta
        }
      };
      const result = await API.get(
        process.env.REACT_APP_HUB_API,
        "/charts/movimientos-cuenta",
        init
      );

      if (result.success) {
        const items = [];
        let balance = 0;

        const thisMonth = new Date(
          new Date(new Date().getFullYear(), new Date().getMonth() - 1, new Date().getDate())
        );

        if (result.data) {
          for (let i = 0; i < result.data.length; i++) {
            const row = result.data[i];
            if (i === 0) {
              balance = result.total;
            } else {
              balance =
                balance - getMonto(result.data[i - 1].ingreso) + result.data[i - 1].comision;
            }

            items.push({
              pedido: row.pedido,
              fecha: row.fecha,
              estado: row.estado,
              descripcion: row.descripcion,
              ingreso: getMonto(row.ingreso),
              comision: row.comision,
              balance
            });
          }
        }

        const movementsThisMonth = items.filter(row => new Date(row.fecha) >= thisMonth);

        setInitialRows(items);
        setRows(movementsThisMonth);
      }
    } catch (err) {
      console.error("fetchPayments", err);
    }
  }, [cuenta]);

  useEffect(() => {
    if (cuenta) {
      fetchPaymentsTotalLastMonth();
      fetchSalesTotalLastMonth();
      fetchMovimientos();
    }
  }, [cuenta, fetchPaymentsTotalLastMonth, fetchSalesTotalLastMonth, fetchMovimientos]);

  const options = ["Hoy", "Ayer", "Últimos 7 días", "Últimos 30 días", "Mes pasado"];

  const handleMenuClick = index => {
    setSelectedIndexPayments(index);
    setAnchorEl(null);
    handlePayments(index);
    handleSales(index);
    handleMovements(index);
  };

  const loadingCircle = () => {
    return (
      <div className={classes.noData}>
        <CircularProgress color="primary" />
      </div>
    );
  };

  const chart = data => {
    if (loading) {
      return loadingCircle();
    }

    if (data[1][0] === 0) {
      return (
        <div className={classes.noData}>
          <div className="text-center mb-3">
            <InfoOutlinedIcon fontSize="large" color="primary" />
          </div>
          <Typography variant="h5">No hay datos para la fecha seleccionada.</Typography>
        </div>
      );
    }

    return (
      <Chart
        chartType="LineChart"
        loader={loadingCircle}
        data={data}
        options={{
          fontSize: 12,
          fontName: "Roboto",
          chartArea: {
            left: 40,
            top: 40,
            width: "80%"
          },
          legend: {
            position: "none"
          },
          hAxis: {
            showTextEvery: data.length - 2
          },
          vAxis: {
            format: "short",
            gridlines: {
              multiple: 1
            },
            viewWindow: {
              min: 0
            }
          }
        }}
      />
    );
  };

  const sortToday = data => {
    const array = data.filter(date => new Date(date[0]) >= today);

    if (array.length > 0) {
      return array;
    } else {
      return [[0, 0]];
    }
  };

  const sortYesterday = data => {
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const array = data.filter(date => new Date(date[0]) >= yesterday && new Date(date[0]) <= today);

    if (array.length > 0) {
      return array;
    } else {
      return [[0, 0]];
    }
  };

  const sortLastWeek = data => {
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);

    const array = data.filter(date => new Date(date[0]) >= lastWeek);

    if (array.length > 0) {
      return array;
    } else {
      return [[0, 0]];
    }
  };

  const sortMonth = data => {
    const thisMonth = new Date(today);
    thisMonth.setMonth(thisMonth.getMonth() - 1);

    const array = data.filter(date => new Date(date[0]) >= thisMonth);

    if (array.length > 0) {
      return array;
    } else {
      return [[0, 0]];
    }
  };

  const sortLastMonth = data => {
    const thisMonth = new Date(today);
    thisMonth.setMonth(thisMonth.getMonth() - 1);
    const lastMonth = new Date(today);
    lastMonth.setMonth(lastMonth.getMonth() - 2);

    const array = data.filter(
      date => new Date(date[0]) >= lastMonth && new Date(date[0]) <= thisMonth
    );

    if (array.length > 0) {
      return array;
    } else {
      return [[0, 0]];
    }
  };

  const handlePayments = index => {
    let payments;

    switch (index) {
      case 0:
        payments = sortToday(paymentsTotalLastMonth);
        setPaymentsChart([["Fecha", "Montos"], ...payments]);
        setTotalPayments(total(payments));
        break;
      case 1:
        payments = sortYesterday(paymentsTotalLastMonth);
        setPaymentsChart([["Fecha", "Montos"], ...payments]);
        setTotalPayments(total(payments));
        break;
      case 2:
        payments = sortLastWeek(paymentsTotalLastMonth);
        setPaymentsChart([["Fecha", "Montos"], ...payments]);
        setTotalPayments(total(payments));
        break;
      case 3:
        payments = sortMonth(paymentsTotalLastMonth);
        setPaymentsChart([["Fecha", "Montos"], ...payments]);
        setTotalPayments(total(payments));
        break;
      case 4:
        payments = sortLastMonth(paymentsTotalLastMonth);
        setPaymentsChart([["Fecha", "Montos"], ...payments]);
        setTotalPayments(total(payments));
        break;
      default:
        break;
    }

    return true;
  };

  const handleSales = index => {
    let sales;

    switch (index) {
      case 0:
        sales = sortToday(salesTotalLastMonth);
        setSalesChart([["Fecha", "Ventas"], ...sales]);
        setTotalSales(total(sales));
        break;
      case 1:
        sales = sortYesterday(salesTotalLastMonth);
        setSalesChart([["Fecha", "Ventas"], ...sales]);
        setTotalSales(total(sales));
        break;
      case 2:
        sales = sortLastWeek(salesTotalLastMonth);
        setSalesChart([["Fecha", "Ventas"], ...sales]);
        setTotalSales(total(sales));
        break;
      case 3:
        sales = sortMonth(salesTotalLastMonth);
        setSalesChart([["Fecha", "Ventas"], ...sales]);
        setTotalSales(total(sales));
        break;
      case 4:
        sales = sortLastMonth(salesTotalLastMonth);
        setSalesChart([["Fecha", "Ventas"], ...sales]);
        setTotalSales(total(sales));
        break;
      default:
        break;
    }

    return true;
  };

  const handleMovements = index => {
    let filter = [];
    const yesterday = new Date(today);
    const lastWeek = new Date(today);
    const thisMonth = new Date(today);
    const lastMonth = new Date(today);

    switch (index) {
      case 0:
        filter = initialRows.filter(row => new Date(row.fecha) >= today);
        setRows(filter);
        break;
      case 1:
        yesterday.setDate(yesterday.getDate() - 1);
        filter = initialRows.filter(
          row => new Date(row.fecha) >= yesterday && new Date(row.fecha) <= today
        );
        setRows(filter);
        break;
      case 2:
        lastWeek.setDate(lastWeek.getDate() - 7);
        filter = initialRows.filter(row => new Date(row.fecha) >= lastWeek);
        setRows(filter);
        break;
      case 3:
        thisMonth.setMonth(thisMonth.getMonth() - 1);
        filter = initialRows.filter(row => new Date(row.fecha) >= thisMonth);
        setRows(filter);
        break;
      case 4:
        thisMonth.setMonth(thisMonth.getMonth() - 1);
        lastMonth.setMonth(lastMonth.getMonth() - 2);
        filter = initialRows.filter(
          row => new Date(row.fecha) >= lastMonth && new Date(row.fecha) <= thisMonth
        );
        setRows(filter);
        break;
      default:
        break;
    }
  };

  const total = data => {
    let total = 0;

    for (let i = 0; i < data.length; i++) {
      total += data[i][1];
    }

    return total;
  };

  const getMonto = value => {
    let monto = value && 0;

    if (typeof value === "string") {
      monto = value.replace('"', "");
      monto = parseInt(monto);
    }

    return monto;
  };

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
  }

  const headCells = [
    { id: "pedido", numeric: false, disablePadding: false, label: "Número de pedido" },
    { id: "fecha", numeric: false, disablePadding: false, label: "Fecha" },
    { id: "estado", numeric: false, disablePadding: false, label: "Estado" },
    { id: "descripcion", numeric: false, disablePadding: false, label: "Descripción" },
    { id: "ingreso", numeric: true, disablePadding: false, label: "Ingreso" },
    { id: "comision", numeric: true, disablePadding: false, label: "Comisión" },
    { id: "balance", numeric: true, disablePadding: false, label: "Balance" }
  ];

  function EnhancedTableHead(props) {
    const { order, orderBy } = props;

    return (
      <TableHead>
        <TableRow>
          {headCells.map(headCell => (
            <TableCell
              key={headCell.id}
              align={headCell.numeric ? "right" : "left"}
              padding={headCell.disablePadding ? "none" : "default"}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {headCell.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  }

  const useToolbarStyles = makeStyles(theme => ({
    root: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    },
    highlight:
      theme.palette.type === "light"
        ? {
            color: theme.palette.secondary.main,
            backgroundColor: lighten(theme.palette.secondary.light, 0.85)
          }
        : {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.secondary.dark
          },
    title: {
      flex: "1"
    }
  }));

  const EnhancedTableToolbar = props => {
    const classes = useToolbarStyles();

    return (
      <Toolbar className={clsx(classes.root)}>
        <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
          Cartola de movimientos
        </Typography>
        <Typography variant="body1" component="div">
          {new Date().toLocaleDateString("es-CL", {
            year: "numeric",
            month: "long",
            day: "numeric"
          })}
        </Typography>
      </Toolbar>
    );
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = name => selected.indexOf(name) !== -1;

  const formatDate = date => {
    const formattedDate = new Date(date);

    return formattedDate.toLocaleString("es-CL");
  };

  return (
    <div>
      <AppBar position="sticky">
        <Toolbar>
          {onBack && (
            <IconButton
              className={classes.backButton}
              edge="start"
              color="inherit"
              onClick={onBack}
            >
              <ArrowBackIcon />
            </IconButton>
          )}

          <Typography className={classes.title} variant="h6">
            Billetera
          </Typography>
        </Toolbar>
      </AppBar>

      <div className={classes.container}>
        {loading ? (
          <div className={classes.empty}>
            <CircularProgress color="primary" />
          </div>
        ) : (
          <>
            <Grid container spacing={0}>
              <Card className={classes.card}>
                <Grid container item xs={12}>
                  <Grid item xs={12} sm={6}>
                    <div className={classes.flexRow}>
                      <div className={classes.topCardPurple}>
                        <Typography className="mb-2" variant="subtitle2">
                          Total
                        </Typography>
                        <Typography variant="subtitle1">
                          <NumberFormat
                            prefix={"$ "}
                            displayType={"text"}
                            thousandSeparator="."
                            decimalSeparator=","
                            value={totalPayments}
                          />
                        </Typography>
                      </div>
                      <div className={classes.topCard}>
                        <Typography className="mb-2" variant="subtitle2" color="textSecondary">
                          Tiempo
                        </Typography>
                        <Typography variant="subtitle1" color="textSecondary">
                          {options[selectedIndexPayments]}
                        </Typography>
                      </div>
                      <Hidden smUp>
                        <div className={classes.options}>
                          <IconButton
                            aria-label="settings"
                            onClick={event => setAnchorEl(event.currentTarget)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                          <Menu
                            id="lock-menu"
                            anchorEl={anchorEl}
                            keepMounted
                            open={Boolean(anchorEl)}
                            onClose={() => setAnchorEl(null)}
                          >
                            {options.map((option, index) => (
                              <MenuItem
                                key={option}
                                selected={index === selectedIndexPayments}
                                onClick={() => handleMenuClick(index)}
                              >
                                {option}
                              </MenuItem>
                            ))}
                          </Menu>
                        </div>
                      </Hidden>
                    </div>
                    {chart(paymentsChart)}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <div className={classes.flexRow}>
                      <div className={classes.topCardPurple}>
                        <Typography className="mb-2" variant="subtitle2">
                          Cantidad de ventas
                        </Typography>
                        <Typography variant="subtitle1">{totalSales}</Typography>
                      </div>
                      <div className={classes.topCard}>
                        <Typography className="mb-2" variant="subtitle2" color="textSecondary">
                          Tiempo
                        </Typography>
                        <Typography variant="subtitle1" color="textSecondary">
                          {options[selectedIndexPayments]}
                        </Typography>
                      </div>
                      <Hidden xsDown>
                        <div className={classes.options}>
                          <IconButton
                            aria-label="settings"
                            onClick={event => setAnchorEl(event.currentTarget)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                          <Menu
                            id="lock-menu"
                            anchorEl={anchorEl}
                            keepMounted
                            open={Boolean(anchorEl)}
                            onClose={() => setAnchorEl(null)}
                          >
                            {options.map((option, index) => (
                              <MenuItem
                                key={option}
                                selected={index === selectedIndexPayments}
                                onClick={() => handleMenuClick(index)}
                              >
                                {option}
                              </MenuItem>
                            ))}
                          </Menu>
                        </div>
                      </Hidden>
                    </div>
                    {chart(salesChart)}
                  </Grid>
                </Grid>
              </Card>

              <Container maxWidth="md">
                <Card className={classes.card}>
                  <Grid item xs={12}>
                    <EnhancedTableToolbar numSelected={selected.length} />
                    <TableContainer>
                      <Table
                        className={classes.table}
                        aria-labelledby="tableTitle"
                        size="medium"
                        aria-label="enhanced table"
                        fixedHeader={true}
                      >
                        <EnhancedTableHead classes={classes} order={order} orderBy={orderBy} />
                        <TableBody>
                          {stableSort(rows, getComparator(order, orderBy))
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, index) => {
                              const isItemSelected = isSelected(row.cuenta);
                              const labelId = `enhanced-table-checkbox-${index}`;

                              return (
                                <TableRow
                                  hover
                                  role="checkbox"
                                  aria-checked={isItemSelected}
                                  tabIndex={-1}
                                  key={`row-${index}`}
                                  selected={isItemSelected}
                                >
                                  <TableCell component="th" id={labelId} scope="row">
                                    {row.pedido}
                                  </TableCell>
                                  <TableCell>{formatDate(row.fecha)}</TableCell>
                                  <TableCell>{row.estado}</TableCell>
                                  <TableCell>{row.descripcion}</TableCell>
                                  <TableCell align="right">
                                    <Chip
                                      classes={{ root: classes.chipIngreso }}
                                      size="small"
                                      label={
                                        <NumberFormat
                                          prefix={"$"}
                                          displayType={"text"}
                                          thousandSeparator="."
                                          decimalSeparator=","
                                          decimalScale={0}
                                          value={row.ingreso}
                                        />
                                      }
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip
                                      classes={{ root: classes.chipComision }}
                                      size="small"
                                      label={
                                        <NumberFormat
                                          prefix={"-$"}
                                          displayType={"text"}
                                          thousandSeparator="."
                                          decimalSeparator=","
                                          decimalScale={0}
                                          value={row.comision}
                                        />
                                      }
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <NumberFormat
                                      prefix={"$"}
                                      displayType={"text"}
                                      thousandSeparator="."
                                      decimalSeparator=","
                                      decimalScale={0}
                                      value={row.balance}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      rowsPerPageOptions={[25, 50]}
                      component="div"
                      count={rows.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onChangePage={handleChangePage}
                      onChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                  </Grid>
                </Card>
              </Container>
            </Grid>
          </>
        )}
      </div>
    </div>
  );
};

export default Billetera;
